import React, { useState, useEffect, useCallback } from 'react';
import { collaborationAPI, dealCoachingAPI, hrAPI } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Send, Trash2, Brain, Loader2, AlertTriangle, Target, Zap, ChevronDown, ChevronUp, AtSign, Eye } from 'lucide-react';
import { formatLocal } from '../lib/dateUtils';

export function DealComments({ dealId }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [team, setTeam] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await collaborationAPI.getComments(dealId);
      setComments(res.data.comments || []);
    } catch {}
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try { const r = await hrAPI.getTeamMembers(); setTeam(Array.isArray(r.data?.data || r.data) ? (r.data?.data || r.data) : []); } catch {}
    })();
  }, []);

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    const mentions = [];
    const mentionRegex = /@(\S+)/g;
    let m;
    while ((m = mentionRegex.exec(text)) !== null) {
      const member = team.find(t => (t.name || t.email || '').toLowerCase().includes(m[1].toLowerCase()));
      if (member) mentions.push(member.id || member.user_id);
    }
    try {
      await collaborationAPI.addComment(dealId, { text, mentions });
      setText('');
      load();
      toast.success('Comment posted');
    } catch { toast.error('Failed to post'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (commentId) => {
    try { await collaborationAPI.deleteComment(dealId, commentId); load(); } catch {}
  };

  const insertMention = (name) => {
    setText(prev => prev + `@${name} `);
    setShowMentions(false);
  };

  return (
    <div className="space-y-3" data-testid="deal-comments">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold">Comments ({comments.length})</h4>
      </div>

      {/* Comment Input */}
      <div className="flex gap-2 relative">
        <div className="flex-1 relative">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handlePost()}
            placeholder="Add a comment... Use @ to mention"
            className="w-full px-3 py-2 text-sm border rounded-lg bg-background"
            data-testid="comment-input"
          />
          <button onClick={() => setShowMentions(!showMentions)} className="absolute right-2 top-2 text-muted-foreground hover:text-primary">
            <AtSign className="h-4 w-4" />
          </button>
          {showMentions && team.length > 0 && (
            <div className="absolute top-full left-0 w-full mt-1 border rounded-lg bg-background shadow-lg z-10 max-h-32 overflow-auto">
              {team.slice(0, 8).map((t, i) => (
                <button key={i} className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted" onClick={() => insertMention(t.name || t.email)}>
                  @{t.name || t.email}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button size="sm" onClick={handlePost} disabled={posting || !text.trim()} data-testid="post-comment-btn">
          {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Comments List */}
      {loading ? <Loader2 className="h-4 w-4 animate-spin text-primary mx-auto" /> : (
        <div className="space-y-2 max-h-48 overflow-auto">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2 p-2 rounded-lg bg-muted/30 group" data-testid={`comment-${c.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{c.author_name}</span>
                  <span className="text-[10px] text-muted-foreground">{c.created_at ? formatLocal(c.created_at) : ''}</span>
                </div>
                <p className="text-sm mt-0.5">{c.text}</p>
                {c.mentions?.length > 0 && <div className="flex gap-1 mt-1">{c.mentions.map((m, i) => <Badge key={i} variant="secondary" className="text-[9px]">@{m.slice(0, 8)}</Badge>)}</div>}
              </div>
              <button onClick={() => handleDelete(c.id)} className="opacity-0 group-hover:opacity-100 text-red-500 transition-opacity"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
          {comments.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No comments yet</p>}
        </div>
      )}
    </div>
  );
}


export function AICoachPanel({ dealId, dealName }) {
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const runCoaching = async () => {
    setLoading(true);
    try {
      const res = await dealCoachingAPI.getCoaching(dealId);
      setCoaching(res.data.coaching);
      setExpanded(true);
    } catch { toast.error('AI coaching failed'); }
    finally { setLoading(false); }
  };

  // Parse coaching response
  let parsed = null;
  if (coaching) {
    try {
      if (typeof coaching === 'string') {
        const jsonMatch = coaching.match(/\{[\s\S]*\}/);
        parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
      } else if (coaching.response) {
        const jsonMatch = coaching.response.match(/\{[\s\S]*\}/);
        parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
      } else {
        parsed = coaching;
      }
    } catch { parsed = null; }
  }

  return (
    <div className="space-y-2" data-testid="ai-coach-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-purple-500" />
          <h4 className="text-sm font-semibold">AI Deal Coach</h4>
        </div>
        <Button size="sm" variant="outline" onClick={runCoaching} disabled={loading} data-testid="run-coaching-btn" className="text-purple-500 border-purple-500/30">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Brain className="h-3.5 w-3.5 mr-1" />}
          {coaching ? 'Re-analyze' : 'Analyze Deal'}
        </Button>
      </div>

      {parsed && expanded && (
        <div className="space-y-2 p-3 rounded-lg bg-purple-500/5 border border-purple-500/20" data-testid="coaching-results">
          {/* Risk & Win Probability */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <AlertTriangle className={`h-3.5 w-3.5 ${(parsed.risk_score || 0) > 6 ? 'text-red-500' : (parsed.risk_score || 0) > 3 ? 'text-amber-500' : 'text-emerald-500'}`} />
              <span className="text-xs font-medium">Risk: {parsed.risk_score}/10</span>
            </div>
            <div className="flex items-center gap-1">
              <Target className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-xs font-medium">Win: {parsed.win_probability}%</span>
            </div>
          </div>

          {/* Summary */}
          {parsed.coaching_summary && <p className="text-xs text-muted-foreground">{parsed.coaching_summary}</p>}

          {/* Pricing */}
          {parsed.pricing_advice && (
            <div className="text-xs"><span className="font-medium text-amber-500">Pricing: </span>{parsed.pricing_advice}</div>
          )}

          {/* Discount */}
          {parsed.suggested_discount && parsed.suggested_discount !== 'none' && (
            <div className="text-xs"><span className="font-medium text-emerald-500">Discount: </span>{parsed.suggested_discount}</div>
          )}

          {/* Next Steps */}
          {parsed.next_steps?.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">Next Steps:</p>
              <ul className="space-y-0.5">{parsed.next_steps.map((s, i) => (
                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1"><Zap className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />{s}</li>
              ))}</ul>
            </div>
          )}

          {/* Risk Factors */}
          {parsed.risk_factors?.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">Risk Factors:</p>
              <ul className="space-y-0.5">{parsed.risk_factors.map((f, i) => (
                <li key={i} className="text-xs text-red-400 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />{f}</li>
              ))}</ul>
            </div>
          )}

          {parsed.approval_notes && <div className="text-xs"><span className="font-medium">Approval: </span>{parsed.approval_notes}</div>}
        </div>
      )}
    </div>
  );
}
